import {
  Button,
  formatDate,
  formatMoney,
  ListView,
  PageHeader,
  pickerDay,
  TextButton,
  useCursorList,
  zonedInstant,
} from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { DatePicker } from "@majitap/ui/arc/date-picker/date-picker";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { Select } from "@majitap/ui/arc/select/select";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { api, errorOf, ok } from "../../api";
import { categoriesQuery, useProductItems } from "../../catalog";
import { useCompanyFormat } from "../../company";
import { messageFor } from "../../error-copy";
import { filterSearch } from "../../list-search";
import { partnerOptionsQuery } from "../../partners";
import { amountText, chargeTypesQuery, RULE_STATE, scopeText, zonesQuery } from "../../pricing";
import { PayerFilterDialog } from "../shared/payer-filter";
import { ChargeTypesView } from "./charge-types";
import styles from "./pricing.module.css";
import { QuoteView } from "./quote";
import { RuleDialog } from "./rule-form";

const VIEWS = ["rules", "types", "quote"] as const;
type View = (typeof VIEWS)[number];

export const pricingFilters = filterSearch({
  view: VIEWS,
  charge_type_id: "text",
  product_item_id: "text",
  geofence_id: "text",
  category_id: "text",
  partner_id: "text",
  // one customer's, business's or place's rules (contract prices), from their page or the search
  customer_id: "text",
  business_id: "text",
  location_id: "text",
  /** "now" (the default), "any", or a day (YYYY-MM-DD) in the company's zone */
  when: "text",
});
type PricingSearch = ReturnType<typeof pricingFilters.validate>;

/** Pricing (P8-19, `Console / Pricing` in the design): rules, charge types and the quote tool. */
export function PricingPage(): ReactNode {
  const search = useSearch({ from: "/console/pricing" });
  const navigate = useNavigate({ from: "/pricing" });
  const view = search.view ?? "rules";
  const [adding, setAdding] = useState(false);
  return (
    <>
      <PageHeader
        title="Pricing"
        description="Which price applies where and to whom, the charges behind it, and what a customer would pay."
        actions={view === "rules" ? <Button onClick={() => setAdding(true)}>New rule</Button> : undefined}
      />
      <SegmentedControl
        label="Show"
        value={view}
        onValueChange={(v) => navigate({ search: { view: v === "rules" ? undefined : (v as View) } })}
        options={[
          { value: "rules", label: "Rules" },
          { value: "types", label: "Charge types" },
          { value: "quote", label: "Quote" },
        ]}
      />
      {view === "rules" ? <RulesList search={search} /> : null}
      {view === "types" ? <ChargeTypesView /> : null}
      {view === "quote" ? <QuoteView /> : null}
      {adding ? <RuleDialog onClose={() => setAdding(false)} /> : null}
    </>
  );
}

function RulesList({ search }: { search: PricingSearch }) {
  const navigate = useNavigate({ from: "/pricing" });
  const format = useCompanyFormat();
  const types = useQuery(chargeTypesQuery);
  const zones = useQuery(zonesQuery);
  const items = useProductItems();
  const when = search.when ?? "now";
  const activeAt =
    when === "any"
      ? undefined
      : when === "now"
        ? new Date().toISOString()
        : zonedInstant(new Date(`${when}T00:00:00`), "12:00", format.timezone);
  const categories = useQuery(categoriesQuery);
  const partners = useQuery(partnerOptionsQuery);
  const [picking, setPicking] = useState(false);
  const query = {
    charge_type_id: search.charge_type_id,
    product_item_id: search.product_item_id,
    geofence_id: search.geofence_id,
    category_id: search.category_id,
    partner_id: search.partner_id,
    customer_id: search.customer_id,
    business_id: search.business_id,
    location_id: search.location_id,
  };
  const scoped = search.customer_id || search.business_id || search.location_id;
  const list = useCursorList({
    queryKey: ["pricing", "rules", query, when],
    fetchPage: (cursor) =>
      ok(api.GET("/v1/admin/pricing", { params: { query: { ...query, active_at: activeAt, cursor, limit: 25 } } })),
  });
  const set = (patch: Partial<PricingSearch>) => navigate({ search: (s) => ({ ...s, ...patch }) });
  const pick = (
    label: string,
    value: string | undefined,
    options: { value: string; label: string }[],
    key: keyof PricingSearch,
  ) => (
    <Select
      label={label}
      value={value ?? "any"}
      onValueChange={(v) => set({ [key]: v === "any" ? undefined : v })}
      options={[{ value: "any", label: `Any ${label.toLowerCase()}` }, ...options]}
    />
  );
  return (
    <ListView
      caption="Pricing rules"
      itemName={{ one: "rule", other: "rules" }}
      rows={list.rows}
      rowKey={(r) => r.id}
      status={list.status}
      errorMessage={list.error ? messageFor(errorOf(list.error)) : undefined}
      onRetry={list.refetch}
      hasMore={list.hasMore}
      loadingMore={list.loadingMore}
      onLoadMore={list.loadMore}
      filtered={pricingFilters.active(search).some(([k]) => k !== "view")}
      onResetFilters={() => navigate({ search: {} })}
      empty={{
        title: "No rules match",
        description: "A price, fee, cost or tax applies once there's a rule for it. Add one with New rule.",
      }}
      toolbar={
        <div className={styles.toolbar}>
          <SegmentedControl
            label="In force"
            value={when === "now" || when === "any" ? when : "day"}
            onValueChange={(v) => set({ when: v === "now" ? undefined : v === "any" ? "any" : pickerDay(new Date()) })}
            options={[
              { value: "now", label: "In force now" },
              { value: "day", label: "On a day" },
              { value: "any", label: "All, any time" },
            ]}
          />
          {when !== "now" && when !== "any" ? (
            <DatePicker
              label="Day"
              value={new Date(`${when}T00:00:00`)}
              onChange={(d) => d && set({ when: pickerDay(d) })}
            />
          ) : null}
          {pick(
            "Charge",
            search.charge_type_id,
            (types.data ?? []).map((t) => ({ value: t.id, label: t.name })),
            "charge_type_id",
          )}
          {pick(
            "Item",
            search.product_item_id,
            items.items.map((i) => ({ value: i.id, label: i.label })),
            "product_item_id",
          )}
          {pick(
            "Zone",
            search.geofence_id,
            (zones.data ?? []).map((z) => ({ value: z.id, label: z.name })),
            "geofence_id",
          )}
          {pick(
            "Category",
            search.category_id,
            (categories.data ?? []).map((c) => ({ value: c.id, label: c.name })),
            "category_id",
          )}
          {pick("Partner", search.partner_id, partners.data ?? [], "partner_id")}
          {search.partner_id ? (
            <Alert tone="warning" title="Partner rates don't apply yet">
              These rules are kept, but MajiTap doesn't use a partner's own rate when it prices a delivery: the zone
              rate applies. Roadmap P8-35.
            </Alert>
          ) : null}
          {scoped ? (
            <span className={styles.muted}>
              Only{" "}
              {list.rows[0]?.scope_names.location ??
                list.rows[0]?.scope_names.business ??
                list.rows[0]?.scope_names.customer ??
                "their"}
              's prices.{" "}
              <TextButton
                onClick={() => set({ customer_id: undefined, business_id: undefined, location_id: undefined })}
              >
                Everyone's
              </TextButton>
            </span>
          ) : (
            <Button variant="secondary" size="sm" onClick={() => setPicking(true)}>
              Customer or business
            </Button>
          )}
          {picking ? (
            <PayerFilterDialog
              title="Whose prices"
              description="Show the rules made for one customer or business: their contract prices."
              action="Show their prices"
              onPick={(payer) => {
                setPicking(false);
                set({ ...payer.billee, when: "any" });
              }}
              onClose={() => setPicking(false)}
            />
          ) : null}
        </div>
      }
      columns={[
        {
          key: "charge_type",
          label: "Charge",
          render: (r) => (
            <Link to="/pricing/$ruleId" params={{ ruleId: r.id }} className={styles.link}>
              {r.charge_type.name}
            </Link>
          ),
        },
        { key: "scope", label: "Applies to", render: (r) => <span className={styles.muted}>{scopeText(r)}</span> },
        {
          key: "amount",
          label: "Amount",
          numeric: true,
          render: (r) => amountText(r, (v) => formatMoney(v, { ...format, currency: r.currency })),
        },
        {
          key: "starts_at",
          label: "From",
          render: (r) => `${formatDate(r.starts_at, format)}${r.ends_at ? ` to ${formatDate(r.ends_at, format)}` : ""}`,
        },
        {
          key: "state",
          label: "State",
          render: (r) => <Badge tone={RULE_STATE[r.state][1]}>{RULE_STATE[r.state][0]}</Badge>,
        },
      ]}
    />
  );
}
